import Link from 'next/link'
import React from 'react'

import { cn } from '@/utilities/ui'
import { DISPLAY } from './tokens'

// VR/Button. Sizes, variants, the bloom and three deliberate oddities: see
// docs/unveiling.md#the-buttons.

const SIZES = {
  default: [
    'gap-2 pl-4 pr-3.5 py-[11px] text-[14px] leading-[14px]',
    'xl:gap-3 xl:pl-6 xl:pr-5 xl:py-[15px] xl:text-[16px] xl:leading-4',
  ].join(' '),
  large: [
    'gap-3 pl-6 pr-5 py-[15px] text-[16px] leading-4',
    'xl:gap-4 xl:pl-8 xl:pr-6 xl:py-[19px] xl:text-[20px] xl:leading-5',
  ].join(' '),
} as const

const COLLAPSE_TO_ICON = 'sr-only md:not-sr-only'

// The bloom is repeated verbatim at each site: Tailwind never sees a class built from a template
// literal, so sharing it as a constant would silently emit no CSS.
const VARIANTS = {
  primary: [
    // Transparent border, not "no border", or primary sits 2px shorter than a ghost beside it.
    'border border-transparent bg-civo-lime text-civo-black',
    'hover:bg-[#00cc59] hover:shadow-[0_0_24px_#00ff6f38,0_0_56px_#00ff6f17]',
  ].join(' '),
  // For a lime surface, where primary would vanish and the base lime focus ring would too.
  dark: 'border border-transparent bg-civo-black text-civo-white hover:bg-[#0a0c0d] focus-visible:outline-civo-black',
  // Ghost's counterpart on a lime surface, where the white outline would vanish.
  darkGhost:
    'border border-civo-black text-civo-black hover:bg-civo-black hover:text-civo-white focus-visible:outline-civo-black',
  ghost:
    'border border-civo-cloud/25 text-civo-white hover:border-civo-white hover:bg-civo-white hover:text-civo-black',
  // Ghost at rest, primary while an ancestor marked `group/panel` is hovered or focused.
  panel: [
    'border border-civo-cloud/25 text-civo-white',
    'group-hover/panel:border-civo-lime group-hover/panel:bg-civo-lime group-hover/panel:text-civo-black',
    // Stacked, not a plain `hover:`, which would lose to the three-deep group rule on specificity.
    'group-hover/panel:hover:bg-[#00cc59] hover:shadow-[0_0_24px_#00ff6f38,0_0_56px_#00ff6f17]',
    'focus-visible:border-civo-lime focus-visible:bg-civo-lime focus-visible:text-civo-black',
    'focus-visible:shadow-[0_0_24px_#00ff6f38,0_0_56px_#00ff6f17]',
  ].join(' '),
} as const

const BASE = [
  'group/btn inline-flex items-center rounded-[2px] font-medium tracking-[-0.01em]',
  // `transition-colors` does not cover box-shadow, so the bloom is named explicitly or it snaps in.
  'transition-[color,background-color,border-color,box-shadow] duration-150 ease-out motion-reduce:transition-none',
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-civo-lime',
].join(' ')

type CommonProps = {
  variant?: keyof typeof VARIANTS
  size?: keyof typeof SIZES
  /** Rendered before the label, e.g. the calendar glyph. Inherits currentColor. */
  leading?: React.ReactNode
  /** Hides the label below `xl` so the button shrinks to its icon. See COLLAPSE_TO_ICON. */
  collapseToIcon?: boolean
  /**
   * Rendered after the label. Defaults to nothing so an arrow is always deliberate. Travels 4px
   * on hover (see `travel`) - a directional affordance, so it applies whatever colour the button becomes.
   */
  trailing?: React.ReactNode
  /** Which way `trailing` travels on hover. Match it to the glyph. */
  travel?: 'right' | 'up'
  className?: string
  children: React.ReactNode
}

type LinkExtras = {
  /** Opens in a new tab with noopener. For a destination we do not control. */
  external?: boolean
}

type Props =
  | (CommonProps & LinkExtras & { href: string; onClick?: never; type?: never })
  | (CommonProps & {
      href?: never
      onClick?: () => void
      type?: 'button' | 'submit'
      external?: never
    })

export function Button({
  variant = 'ghost',
  size = 'default',
  leading,
  trailing,
  travel = 'right',
  collapseToIcon,
  className,
  children,
  ...rest
}: Props) {
  const classes = cn(BASE, DISPLAY, SIZES[size], VARIANTS[variant], className)
  const inner = (
    <>
      {leading}
      {collapseToIcon ? <span className={COLLAPSE_TO_ICON}>{children}</span> : children}
      {trailing ? (
        <span
          className={cn(
            'transition-transform duration-150 ease-out motion-reduce:transition-none',
            travel === 'up'
              ? 'group-hover/btn:-translate-y-1 motion-reduce:group-hover/btn:translate-y-0'
              : 'group-hover/btn:translate-x-1 motion-reduce:group-hover/btn:translate-x-0',
          )}
        >
          {trailing}
        </span>
      ) : null}
    </>
  )

  if ('href' in rest && rest.href) {
    const external = 'external' in rest && rest.external
    return (
      <Link
        href={rest.href}
        className={classes}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {inner}
      </Link>
    )
  }

  const { onClick, type = 'button' } = rest as { onClick?: () => void; type?: 'button' | 'submit' }
  return (
    <button type={type} onClick={onClick} className={classes}>
      {inner}
    </button>
  )
}
